/**
 * Engine Parameters Form Component
 *
 * Renders provider-specific parameter controls for image generation models.
 * Supports textarea, slider, number, checkbox, and select control types.
 */

import { Info } from 'lucide-react';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Slider } from '@/components/ui/slider';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  ProviderParams,
  ProviderParamDefinition,
  getProviderParamsForModel,
} from '../types/image-generator.types';

export interface EngineParamsFormProps {
  modelName: string;
  providerParams: ProviderParams;
  onUpdate: (params: ProviderParams) => void;
  /** Override which definitions to render (used by Advanced Applicability section) */
  definitions?: ProviderParamDefinition[];
  /** When true, all params default to "not set" instead of their defaultValue */
  allowUnset?: boolean;
  /** Available models for entity-driven param lookup */
  availableModels?: { modelName: string; capabilities: Record<string, unknown> }[];
}

export function EngineParamsForm({
  modelName,
  providerParams,
  onUpdate,
  definitions,
  allowUnset = false,
  availableModels,
}: EngineParamsFormProps) {
  const defs = definitions ?? getProviderParamsForModel(modelName, availableModels);

  if (defs.length === 0) {
    return (
      <p className="text-xs text-muted-foreground italic">
        No engine-specific parameters for this model.
      </p>
    );
  }

  const handleChange = (key: string, value: unknown) => {
    onUpdate({ ...providerParams, [key]: value });
  };

  return (
    <div className="space-y-4">
      {defs.map((def) => (
        <EngineParamField
          key={def.key}
          definition={def}
          value={providerParams[def.key]}
          onChange={(value) => handleChange(def.key, value)}
          allowUnset={allowUnset}
        />
      ))}
    </div>
  );
}

interface EngineParamFieldProps {
  definition: ProviderParamDefinition;
  value: unknown;
  onChange: (value: unknown) => void;
  allowUnset?: boolean;
}

function EngineParamField({
  definition,
  value,
  onChange,
  allowUnset = false,
}: EngineParamFieldProps) {
  const isUnset = allowUnset && (value === undefined || value === '' || value === null);
  return (
    <div className="space-y-1.5">
      <div className="flex items-center gap-1.5">
        <label className="text-xs font-medium">{definition.label}</label>
        <div className="relative group/info">
          <Info className="h-3 w-3 text-muted-foreground cursor-help" />
          <div className="absolute bottom-full right-0 mb-2 px-3 py-2 bg-popover border border-border rounded-lg shadow-lg text-xs text-popover-foreground w-64 opacity-0 invisible group-hover/info:opacity-100 group-hover/info:visible transition-all z-[100]">
            {definition.description}
          </div>
        </div>
      </div>

      {definition.controlType === 'textarea' && (
        <Textarea
          value={(value as string) ?? ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Enter text..."
          className="text-xs min-h-[60px] resize-none"
          rows={2}
        />
      )}

      {definition.controlType === 'slider' && (
        <div className="flex items-center gap-3">
          <Slider
            value={[
              isUnset ? Number(definition.defaultValue) : Number(value ?? definition.defaultValue),
            ]}
            onValueChange={([v]) => onChange(v)}
            min={definition.min}
            max={definition.max}
            step={definition.step}
            className={`flex-1 ${isUnset ? 'opacity-40' : ''}`}
          />
          <span className="text-xs text-muted-foreground w-10 text-right tabular-nums">
            {isUnset ? '–' : Number(value ?? definition.defaultValue)}
          </span>
          {allowUnset && !isUnset && (
            <button
              type="button"
              onClick={() => onChange('')}
              className="text-xs text-muted-foreground hover:text-foreground"
              title="Clear"
            >
              ✕
            </button>
          )}
        </div>
      )}

      {definition.controlType === 'number' && (
        <Input
          type="number"
          value={value !== undefined && value !== '' ? String(value) : ''}
          onChange={(e) => {
            const v = e.target.value;
            if (v === '') {
              onChange('');
            } else {
              const num = Number(v);
              if (
                !isNaN(num) &&
                num >= (definition.min ?? 0) &&
                num <= (definition.max ?? Infinity)
              ) {
                onChange(num);
              }
            }
          }}
          placeholder={`0 – ${definition.max?.toLocaleString()}`}
          className="text-xs h-8"
          min={definition.min}
          max={definition.max}
        />
      )}

      {definition.controlType === 'checkbox' && (
        <div className="flex items-center gap-2">
          <button
            type="button"
            role="checkbox"
            aria-checked={isUnset ? false : !!value}
            onClick={() => onChange(isUnset ? true : !value)}
            className={`h-5 w-5 rounded border flex items-center justify-center transition-colors flex-shrink-0 ${
              isUnset
                ? 'border-muted-foreground/30 opacity-40'
                : value
                  ? 'bg-primary border-primary'
                  : 'border-muted-foreground/50 hover:border-muted-foreground'
            }`}
          >
            {!isUnset && !!value && (
              <svg
                className="h-3.5 w-3.5 text-primary-foreground"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="20 6 9 17 4 12" />
              </svg>
            )}
          </button>
          {isUnset && <span className="text-xs text-muted-foreground italic">Not set</span>}
          {allowUnset && !isUnset && (
            <button
              type="button"
              onClick={() => onChange('')}
              className="text-xs text-muted-foreground hover:text-foreground"
              title="Clear"
            >
              ✕
            </button>
          )}
        </div>
      )}

      {definition.controlType === 'select' && definition.options && (
        <Select
          value={
            allowUnset && isUnset
              ? '__unset__'
              : ((value as string) ?? String(definition.defaultValue))
          }
          onValueChange={(v) => onChange(v === '__unset__' ? '' : v)}
        >
          <SelectTrigger className={`text-xs h-8 ${isUnset ? 'text-muted-foreground' : ''}`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {allowUnset && (
              <SelectItem value="__unset__" className="text-muted-foreground italic">
                Not set
              </SelectItem>
            )}
            {definition.options.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
    </div>
  );
}
